import { useParams, useNavigate } from 'react-router-dom';
import { useProductDetail } from '../hooks/useProductDetail';
import { useProductScores } from '../hooks/useProductScores';
import { ScoreBreakdownView } from '../components/ScoreBreakdownView';
import { ScoreBadge } from '../components/ScoreBadge';
import { ApiClientError } from '@shared/api/client';
import { ForbiddenScreen } from '@shared/components/ForbiddenScreen';
import { NotFoundView } from '@shared/components/NotFoundView';

export function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: product, isLoading: productLoading, error: productError } = useProductDetail(id);
  const { data: scores, isLoading: scoresLoading } = useProductScores(id);

  if (productLoading || scoresLoading) {
    return <div className="text-center py-8 text-gray-500">Loading product...</div>;
  }

  if (productError instanceof ApiClientError && productError.status === 403) {
    return <ForbiddenScreen message={productError.message} />;
  }

  if (productError instanceof ApiClientError && productError.status === 404) {
    return (
      <NotFoundView
        message="Product not found. It may have been removed from the catalog."
        backTo="/products"
        backLabel="Back to Products"
      />
    );
  }

  if (!product) {
    return <div className="text-center py-8 text-gray-500">Product not found</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/products')} className="btn btn-outline text-sm">
          Back
        </button>
        <h2 className="text-xl font-semibold text-gray-900 truncate">{product.title}</h2>
      </div>

      <div className="card p-6">
        <div className="flex items-start gap-6">
          {product.images?.[0] && (
            <img
              src={product.images[0].url}
              alt={product.title}
              className="w-32 h-32 rounded-lg object-cover flex-shrink-0"
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-lg font-medium text-gray-900">{product.title}</h3>
              <ScoreBadge score={product.overallScore} size="md" />
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
              <div>
                Category: <span className="text-gray-900">{product.category}</span>
              </div>
              <div>
                Price:{' '}
                <span className="text-gray-900">
                  {product.price} {product.currency}
                </span>
              </div>
              <div>
                External ID: <span className="text-gray-900">{product.externalId}</span>
              </div>
              <div>
                Last synced:{' '}
                <span className="text-gray-900">
                  {new Date(product.lastSyncedAt).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {scores && (
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Score Breakdown</h3>
          <ScoreBreakdownView data={scores} />
        </div>
      )}
    </div>
  );
}
